// The pure core of the DataTable column filter.
//
// This file holds no React. It holds the filter model, the matching rules, the
// list of distinct values for the value picker, the chip text, and the URL
// grammar. The test env is node with no DOM, so all logic that can be pure is
// pure and is tested here.
//
// The model is keyed by `DataTableColumn.id`. An absent key means "no filter".
// A column filter is one of two kinds. `conditions` holds one or two rules such
// as "greater than 10,000". `values` holds a set of ticked value labels, as an
// Excel column filter does.

import { formatCurrencyGBP, formatCurrencyGBP2dp } from '@/lib/format';
import type { DataTableColumn } from './DataTable';

export type FilterType = 'text' | 'number' | 'date' | 'enum';

export type TextOp =
    | 'contains'
    | 'notContains'
    | 'equals'
    | 'notEquals'
    | 'startsWith'
    | 'endsWith'
    | 'blank'
    | 'notBlank';
export type NumberOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'blank' | 'notBlank';
export type DateOp = 'on' | 'before' | 'after' | 'between' | 'blank' | 'notBlank';
export type DatePreset = 'today' | 'thisWeek' | 'thisMonth' | 'overdue';

export type FilterCondition =
    | { type: 'text'; op: TextOp; value?: string }
    | { type: 'number'; op: NumberOp; value?: number; to?: number }
    | { type: 'date'; op: DateOp; value?: string; to?: string }
    | { type: 'date'; op: 'preset'; preset: DatePreset };

export type ColumnFilter =
    | { kind: 'conditions'; first: FilterCondition; join?: 'and' | 'or'; second?: FilterCondition }
    | { kind: 'values'; values: readonly string[]; includeBlanks: boolean };

/** Keyed by `DataTableColumn.id`. An absent key means no filter. */
export type ColumnFilterModel = Readonly<Record<string, ColumnFilter>>;

export type FilterScalar = string | number | Date | null;

export interface ResolvedFilterSpec<T> {
    type: FilterType;
    /** The normalised value. An empty string becomes null. */
    value: (row: T) => FilterScalar;
    /** The text of the value in the value list and in the chip. */
    label: (row: T) => string;
    enumOptions?: readonly { value: string; label: string }[];
}

/** How many rows `resolveFilterSpec` reads to infer the type of a column. */
const INFERENCE_SAMPLE_SIZE = 200;
/** The most entries the value picker shows at one time. */
export const VALUE_LIST_DISPLAY_CAP = 500;

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;
const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONEY_HEADER = /£|AUM|Value/;

// ---------------------------------------------------------------------------
// Operator copy. The popover and the chip text both read these tables.
// ---------------------------------------------------------------------------

// The words and the order follow the text filters of Tercero, so a reader
// who knows that grid finds each operator in the same place.
export const TEXT_OPERATORS: readonly { op: TextOp; label: string }[] = [
    { op: 'equals', label: 'Equals' },
    { op: 'notEquals', label: 'Does Not Equal' },
    { op: 'startsWith', label: 'Begins With' },
    { op: 'endsWith', label: 'Ends With' },
    { op: 'contains', label: 'Contains' },
    { op: 'notContains', label: 'Does Not Contain' },
    { op: 'blank', label: 'Is Blank' },
    { op: 'notBlank', label: 'Is Not Blank' },
];

export const NUMBER_OPERATORS: readonly { op: NumberOp; label: string }[] = [
    { op: 'eq', label: 'Equals' },
    { op: 'ne', label: 'Does Not Equal' },
    { op: 'gt', label: 'Greater Than' },
    { op: 'gte', label: 'Greater Than Or Equal To' },
    { op: 'lt', label: 'Less Than' },
    { op: 'lte', label: 'Less Than Or Equal To' },
    { op: 'between', label: 'Between' },
    { op: 'blank', label: 'Is Blank' },
    { op: 'notBlank', label: 'Is Not Blank' },
];

export const DATE_OPERATORS: readonly { op: DateOp; label: string }[] = [
    { op: 'on', label: 'On' },
    { op: 'before', label: 'Before' },
    { op: 'after', label: 'After' },
    { op: 'between', label: 'Between' },
    { op: 'blank', label: 'Is Blank' },
    { op: 'notBlank', label: 'Is Not Blank' },
];

export const DATE_PRESETS: readonly { preset: DatePreset; label: string }[] = [
    { preset: 'today', label: 'Today' },
    { preset: 'thisWeek', label: 'This week' },
    { preset: 'thisMonth', label: 'This month' },
    { preset: 'overdue', label: 'Overdue' },
];

/** The operators of a type, for the operator select. */
export function operatorsFor(type: FilterType): readonly { op: string; label: string }[] {
    if (type === 'number') return NUMBER_OPERATORS;
    if (type === 'date') return DATE_OPERATORS;
    return TEXT_OPERATORS;
}

// ---------------------------------------------------------------------------
// Value normalisation
// ---------------------------------------------------------------------------

function normalise(v: FilterScalar | undefined): FilterScalar {
    if (v === undefined || v === null) return null;
    if (typeof v === 'string' && v.trim() === '') return null;
    // NaN and the infinities are sentinels of a sort key, not values.
    if (typeof v === 'number' && !Number.isFinite(v)) return null;
    return v;
}

function toNumber(v: FilterScalar): number | null {
    if (v === null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (v instanceof Date) {
        const t = v.getTime();
        return Number.isNaN(t) ? null : t;
    }
    const n = Number(v.replace(/[£,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
}

/** A calendar day as a whole number, so two days compare with no time zone. */
function dayIndex(year: number, monthIndex: number, day: number): number {
    return Math.floor(Date.UTC(year, monthIndex, day) / 86_400_000);
}

function dayIndexOfDate(d: Date): number | null {
    if (Number.isNaN(d.getTime())) return null;
    return dayIndex(d.getFullYear(), d.getMonth(), d.getDate());
}

/** A yyyy-mm-dd string as a day number, or null when it is not a real day. */
function parseIsoDay(raw: string | undefined): number | null {
    if (!raw) return null;
    const m = ISO_DATE_ONLY.exec(raw);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = Number(m[3]);
    const probe = new Date(Date.UTC(year, month, day));
    if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month) return null;
    if (probe.getUTCDate() !== day) return null;
    return dayIndex(year, month, day);
}

function toDay(v: FilterScalar): number | null {
    if (v === null) return null;
    if (v instanceof Date) return dayIndexOfDate(v);
    if (typeof v === 'number') return dayIndexOfDate(new Date(v));
    const dateOnly = parseIsoDay(v);
    if (dateOnly !== null) return dateOnly;
    return dayIndexOfDate(new Date(v));
}

function isoOfDay(day: number): string {
    return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

function formatIsoDay(iso: string): string {
    const day = parseIsoDay(iso);
    if (day === null) return iso;
    const [y, m, d] = isoOfDay(day).split('-');
    return `${d}/${m}/${y}`;
}

function formatNumber(n: number): string {
    return n.toLocaleString('en-GB');
}

function headerText(header: unknown, fallback: string): string {
    if (typeof header === 'string') return header;
    if (typeof header === 'number') return String(header);
    return fallback;
}

// ---------------------------------------------------------------------------
// Spec resolution
// ---------------------------------------------------------------------------

function inferType(values: readonly FilterScalar[], numeric: boolean): FilterType {
    if (numeric) return 'number';
    const present = values.filter((v): v is Exclude<FilterScalar, null> => v !== null);
    if (present.length === 0) return 'text';
    if (
        present.every(
            (v) => v instanceof Date || (typeof v === 'string' && ISO_DATE_PREFIX.test(v)),
        )
    ) {
        return 'date';
    }
    if (present.every((v) => typeof v === 'number')) return 'number';
    return 'text';
}

/**
 * Work out the filter of one column, or null when the column has none.
 *
 * A column has no filter when `filterable` is false, or when it has neither a
 * `filterValue` nor a `sortValue`. The type comes from `filterType`, then
 * `enumOptions`, then `numeric`, then the shape of a sample of the values.
 */
export function resolveFilterSpec<T>(
    col: DataTableColumn<T>,
    sample: readonly T[],
): ResolvedFilterSpec<T> | null {
    if (col.filterable === false) return null;
    const getter = col.filterValue ?? col.sortValue;
    if (!getter) return null;
    const value = (row: T): FilterScalar => normalise(getter(row));

    let type: FilterType;
    if (col.filterType) type = col.filterType;
    else if (col.enumOptions) type = 'enum';
    else {
        type = inferType(sample.slice(0, INFERENCE_SAMPLE_SIZE).map(value), Boolean(col.numeric));
    }

    const money = MONEY_HEADER.test(headerText(col.header, ''));
    const options = col.enumOptions;

    const defaultLabel = (v: FilterScalar): string => {
        if (v === null) return '';
        if (type === 'date') {
            const day = toDay(v);
            return day === null ? String(v) : formatIsoDay(isoOfDay(day));
        }
        if (type === 'number') {
            const n = toNumber(v);
            if (n === null) return String(v);
            if (!money) return formatNumber(n);
            return Number.isInteger(n) ? formatCurrencyGBP(n) : formatCurrencyGBP2dp(n);
        }
        if (type === 'enum' && options) {
            const found = options.find((o) => o.value === String(v));
            if (found) return found.label;
        }
        return v instanceof Date ? v.toLocaleDateString('en-GB') : String(v);
    };

    const label = col.filterLabel
        ? (row: T): string => col.filterLabel!(row)
        : (row: T): string => defaultLabel(value(row));

    return { type, value, label, ...(options ? { enumOptions: options } : {}) };
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

function isBlankScalar(v: FilterScalar): boolean {
    return v === null || (typeof v === 'string' && v.trim() === '');
}

function scalarText(v: FilterScalar): string {
    if (v === null) return '';
    if (v instanceof Date) return v.toLocaleDateString('en-GB');
    return String(v).trim().toLowerCase();
}

/** True when a condition holds every input it needs. */
export function isFilterComplete(c: FilterCondition): boolean {
    if (c.type === 'text') {
        if (c.op === 'blank' || c.op === 'notBlank') return true;
        return typeof c.value === 'string' && c.value.trim() !== '';
    }
    if (c.type === 'number') {
        if (c.op === 'blank' || c.op === 'notBlank') return true;
        const has = (n: number | undefined) => typeof n === 'number' && Number.isFinite(n);
        return c.op === 'between' ? has(c.value) && has(c.to) : has(c.value);
    }
    if (c.op === 'preset') return true;
    if (c.op === 'blank' || c.op === 'notBlank') return true;
    if (c.op === 'between') return parseIsoDay(c.value) !== null && parseIsoDay(c.to) !== null;
    return parseIsoDay(c.value) !== null;
}

function startOfWeek(today: number): number {
    // Day 0 is a Thursday (1 January 1970). Monday is (day + 3) % 7 === 0.
    return today - ((((today + 3) % 7) + 7) % 7);
}

function matchesCondition(v: FilterScalar, c: FilterCondition, now: Date): boolean {
    const blank = isBlankScalar(v);
    if (c.type === 'text') {
        if (c.op === 'blank') return blank;
        if (c.op === 'notBlank') return !blank;
        const needle = (c.value ?? '').trim().toLowerCase();
        const hay = scalarText(v);
        switch (c.op) {
            case 'contains':
                return !blank && hay.includes(needle);
            case 'notContains':
                return blank || !hay.includes(needle);
            case 'equals':
                return !blank && hay === needle;
            case 'notEquals':
                return blank || hay !== needle;
            case 'startsWith':
                return !blank && hay.startsWith(needle);
            case 'endsWith':
                return !blank && hay.endsWith(needle);
        }
    }
    if (c.type === 'number') {
        if (c.op === 'blank') return blank;
        if (c.op === 'notBlank') return !blank;
        const n = toNumber(v);
        if (c.op === 'ne') return n === null || n !== c.value;
        if (n === null || c.value === undefined) return false;
        switch (c.op) {
            case 'eq':
                return n === c.value;
            case 'gt':
                return n > c.value;
            case 'gte':
                return n >= c.value;
            case 'lt':
                return n < c.value;
            case 'lte':
                return n <= c.value;
            case 'between': {
                if (c.to === undefined) return false;
                return n >= Math.min(c.value, c.to) && n <= Math.max(c.value, c.to);
            }
        }
    }
    if (c.type === 'date') {
        if (c.op === 'blank') return blank;
        if (c.op === 'notBlank') return !blank;
        const day = toDay(v);
        if (day === null) return false;
        const today = dayIndex(now.getFullYear(), now.getMonth(), now.getDate());
        if (c.op === 'preset') {
            switch (c.preset) {
                case 'today':
                    return day === today;
                case 'thisWeek': {
                    const start = startOfWeek(today);
                    return day >= start && day <= start + 6;
                }
                case 'thisMonth':
                    return (
                        day >= dayIndex(now.getFullYear(), now.getMonth(), 1) &&
                        day <= dayIndex(now.getFullYear(), now.getMonth() + 1, 0)
                    );
                case 'overdue':
                    return day < today;
            }
        }
        const a = parseIsoDay(c.value);
        if (a === null) return false;
        switch (c.op) {
            case 'on':
                return day === a;
            case 'before':
                return day < a;
            case 'after':
                return day > a;
            case 'between': {
                const b = parseIsoDay(c.to);
                if (b === null) return false;
                return day >= Math.min(a, b) && day <= Math.max(a, b);
            }
        }
    }
    return false;
}

const valueSetCache = new WeakMap<readonly string[], ReadonlySet<string>>();

function valueSetOf(values: readonly string[]): ReadonlySet<string> {
    let set = valueSetCache.get(values);
    if (!set) {
        set = new Set(values);
        valueSetCache.set(values, set);
    }
    return set;
}

/**
 * Does one value pass one column filter? `key` gives the text that the value
 * list shows for this value. A `values` filter compares that text. An
 * incomplete first condition passes every value.
 */
export function matchesFilter(
    v: FilterScalar,
    f: ColumnFilter,
    _spec: { type: FilterType },
    now: Date,
    key: (v: FilterScalar) => string,
): boolean {
    if (f.kind === 'values') {
        const k = key(v);
        if (isBlankScalar(v) || k.trim() === '') return f.includeBlanks;
        return valueSetOf(f.values).has(k);
    }
    if (!isFilterComplete(f.first)) return true;
    const first = matchesCondition(v, f.first, now);
    if (!f.second || !isFilterComplete(f.second)) return first;
    const second = matchesCondition(v, f.second, now);
    return f.join === 'or' ? first || second : first && second;
}

/**
 * Keep the rows that pass every active column filter. Columns combine with
 * AND. A model key with no spec is ignored. `exceptColumnId` leaves one column
 * out, so the value list of that column can cascade the Excel way.
 */
export function applyColumnFilters<T>(
    rows: readonly T[],
    model: ColumnFilterModel,
    specs: ReadonlyMap<string, ResolvedFilterSpec<T>>,
    now: Date,
    exceptColumnId?: string,
): T[] {
    const active: { filter: ColumnFilter; spec: ResolvedFilterSpec<T> }[] = [];
    for (const [id, filter] of Object.entries(model)) {
        if (id === exceptColumnId) continue;
        const spec = specs.get(id);
        if (spec) active.push({ filter, spec });
    }
    if (active.length === 0) return [...rows];
    return rows.filter((row) =>
        active.every(({ filter, spec }) =>
            matchesFilter(spec.value(row), filter, spec, now, () => spec.label(row)),
        ),
    );
}

// ---------------------------------------------------------------------------
// Distinct values
// ---------------------------------------------------------------------------

export interface DistinctValues {
    entries: { key: string; label: string; count: number }[];
    blanks: number;
    truncated: boolean;
}

/**
 * The values filter that the value list would apply. With a search, only the
 * ticked entries among the matches stay, as in Excel. Without a search, a list
 * where every entry is ticked means no filter (undefined).
 */
export function valuesDraft(
    distinct: DistinctValues,
    ticked: ReadonlySet<string>,
    blanksTicked: boolean,
    search: string,
): ColumnFilter | undefined {
    const needle = search.trim().toLowerCase();
    if (needle !== '') {
        const matches = distinct.entries.filter((e) => e.label.toLowerCase().includes(needle));
        return {
            kind: 'values',
            values: matches.filter((e) => ticked.has(e.key)).map((e) => e.key),
            includeBlanks: distinct.blanks > 0 && '(blanks)'.includes(needle) && blanksTicked,
        };
    }
    const everyEntry = distinct.entries.every((e) => ticked.has(e.key));
    const everyBlank = distinct.blanks === 0 || blanksTicked;
    if (everyEntry && everyBlank && !distinct.truncated) return undefined;
    return {
        kind: 'values',
        values: distinct.entries.filter((e) => ticked.has(e.key)).map((e) => e.key),
        includeBlanks: blanksTicked && distinct.blanks > 0,
    };
}

/** Sort key of a value, for the number and date value lists. */
function orderOf(v: FilterScalar, type: FilterType): number | null {
    if (type === 'number') return toNumber(v);
    if (type === 'date') return toDay(v);
    return null;
}

/**
 * The distinct labels of a column with the count of rows for each one. The
 * list sorts by count for an enum, by value for a number or a date, and by
 * label for text. `cap` limits the entries. `truncated` says that it did.
 */
export function distinctValues<T>(
    rows: readonly T[],
    spec: ResolvedFilterSpec<T>,
    cap = 500,
): DistinctValues {
    const found = new Map<string, { label: string; count: number; order: number | null }>();
    let blanks = 0;
    for (const row of rows) {
        const v = spec.value(row);
        const label = spec.label(row);
        if (isBlankScalar(v) || label.trim() === '') {
            blanks += 1;
            continue;
        }
        const seen = found.get(label);
        if (seen) seen.count += 1;
        else found.set(label, { label, count: 1, order: orderOf(v, spec.type) });
    }
    const all = [...found.values()];
    if (spec.type === 'enum') {
        all.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'en-GB'));
    } else if (spec.type === 'number' || spec.type === 'date') {
        all.sort(
            (a, b) =>
                (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY) ||
                a.label.localeCompare(b.label, 'en-GB'),
        );
    } else {
        all.sort((a, b) => a.label.localeCompare(b.label, 'en-GB', { numeric: true }));
    }
    const kept = all.slice(0, cap);
    return {
        entries: kept.map((e) => ({ key: e.label, label: e.label, count: e.count })),
        blanks,
        truncated: all.length > cap,
    };
}

// ---------------------------------------------------------------------------
// Chip text
// ---------------------------------------------------------------------------

const OP_TEXT: Record<string, string> = {
    contains: 'contains',
    notContains: 'does not contain',
    equals: 'equals',
    notEquals: 'does not equal',
    startsWith: 'begins with',
    endsWith: 'ends with',
    eq: 'equal to',
    ne: 'not equal to',
    gt: 'greater than',
    gte: 'greater than or equal to',
    lt: 'less than',
    lte: 'less than or equal to',
    on: 'on',
    before: 'before',
    after: 'after',
    blank: 'is blank',
    notBlank: 'is not blank',
};

const PRESET_TEXT: Record<DatePreset, string> = {
    today: 'today',
    thisWeek: 'this week',
    thisMonth: 'this month',
    overdue: 'overdue',
};

function describeCondition(c: FilterCondition): string {
    if (c.type === 'date' && c.op === 'preset') return PRESET_TEXT[c.preset];
    if (c.op === 'blank' || c.op === 'notBlank') return OP_TEXT[c.op];
    if (c.type === 'text') return `${OP_TEXT[c.op]} "${c.value ?? ''}"`;
    if (c.type === 'number') {
        const a = c.value === undefined ? '' : formatNumber(c.value);
        if (c.op === 'between')
            return `between ${a} and ${c.to === undefined ? '' : formatNumber(c.to)}`;
        return `${OP_TEXT[c.op]} ${a}`;
    }
    const a = c.value ? formatIsoDay(c.value) : '';
    if (c.op === 'between') return `between ${a} and ${c.to ? formatIsoDay(c.to) : ''}`;
    return `${OP_TEXT[c.op]} ${a}`;
}

/** Short plain text for a filter, for a chip or a tooltip. */
export function describeFilter(f: ColumnFilter, _type?: FilterType): string {
    if (f.kind === 'values') {
        const parts = [...f.values];
        if (f.includeBlanks) parts.push('(Blanks)');
        if (parts.length === 0) return 'no values';
        if (parts.length <= 3) return parts.join(', ');
        return `${parts.length} values`;
    }
    const first = describeCondition(f.first);
    if (!f.second) return first;
    return `${first} ${f.join === 'or' ? 'or' : 'and'} ${describeCondition(f.second)}`;
}

// ---------------------------------------------------------------------------
// Input parsing
// ---------------------------------------------------------------------------

/** Read a number the way a person types it: `£1,250`, `1.5k`, `2m`. */
export function parseNumberInput(raw: string): number | null {
    const cleaned = raw.replace(/[£,\s]/g, '').toLowerCase();
    const m = /^(-?(?:\d+(?:\.\d+)?|\.\d+))([km]?)$/.exec(cleaned);
    if (!m) return null;
    const base = Number(m[1]);
    const factor = m[2] === 'k' ? 1_000 : m[2] === 'm' ? 1_000_000 : 1;
    const n = Math.round(base * factor * 1e8) / 1e8;
    return Number.isFinite(n) ? n : null;
}

/** The raw inputs of one condition row in the popover. */
export interface ConditionInput {
    op: string;
    a: string;
    b: string;
    preset?: DatePreset;
}

/** Build a condition from the popover inputs, or null while they are incomplete. */
export function conditionFromInput(
    type: FilterType,
    input: ConditionInput,
): FilterCondition | null {
    const { op, a, b } = input;
    const bare = op === 'blank' || op === 'notBlank';
    let cond: FilterCondition;
    if (type === 'date') {
        if (op === 'preset') {
            if (!input.preset) return null;
            cond = { type: 'date', op: 'preset', preset: input.preset };
        } else if (bare) cond = { type: 'date', op: op as DateOp };
        else if (op === 'between') cond = { type: 'date', op: 'between', value: a, to: b };
        else cond = { type: 'date', op: op as DateOp, value: a };
    } else if (type === 'number') {
        if (bare) cond = { type: 'number', op: op as NumberOp };
        else {
            const n = parseNumberInput(a);
            if (n === null) return null;
            if (op === 'between') {
                const to = parseNumberInput(b);
                if (to === null) return null;
                cond = { type: 'number', op: 'between', value: n, to };
            } else cond = { type: 'number', op: op as NumberOp, value: n };
        }
    } else {
        cond = bare
            ? { type: 'text', op: op as TextOp }
            : { type: 'text', op: op as TextOp, value: a.trim() };
    }
    return isFilterComplete(cond) ? cond : null;
}

/** The popover inputs of a condition. The reverse of `conditionFromInput`. */
export function inputFromCondition(c: FilterCondition): ConditionInput {
    if (c.type === 'date') {
        if (c.op === 'preset') return { op: 'preset', a: '', b: '', preset: c.preset };
        return { op: c.op, a: c.value ?? '', b: c.to ?? '' };
    }
    if (c.type === 'number') {
        return {
            op: c.op,
            a: c.value === undefined ? '' : String(c.value),
            b: c.to === undefined ? '' : String(c.to),
        };
    }
    return { op: c.op, a: c.value ?? '', b: '' };
}

// ---------------------------------------------------------------------------
// URL grammar
//
//   ct:smith  nc:smith  eq:x  ne:x  sw:x  ew:x      text
//   eq:5  ne:5  gt:10000  gte:5  lt:5  lte:5  bt:100~500   number
//   on:2026-09-29  bf:..  af:..  bt:a~b  pre:overdue        date
//   bl  nbl                                                 any type
//   v:Active|Pending|_b                                     values (_b = blanks)
//   a:gt:100;lt:500   o:...                                 two conditions
//
// Each value part goes through `encodeURIComponent`. A value that starts with
// an underscore also escapes it, so it never reads as the blank marker.
// ---------------------------------------------------------------------------

const BLANK_MARK = '_b';

function enc(s: string): string {
    return encodeURIComponent(s).replace(/^_/, '%5F');
}

function dec(s: string): string | null {
    try {
        return decodeURIComponent(s);
    } catch {
        return null;
    }
}

const TEXT_CODES: Record<TextOp, string> = {
    contains: 'ct',
    notContains: 'nc',
    equals: 'eq',
    notEquals: 'ne',
    startsWith: 'sw',
    endsWith: 'ew',
    blank: 'bl',
    notBlank: 'nbl',
};
const NUMBER_CODES: Record<NumberOp, string> = {
    eq: 'eq',
    ne: 'ne',
    gt: 'gt',
    gte: 'gte',
    lt: 'lt',
    lte: 'lte',
    between: 'bt',
    blank: 'bl',
    notBlank: 'nbl',
};
const DATE_CODES: Record<DateOp, string> = {
    on: 'on',
    before: 'bf',
    after: 'af',
    between: 'bt',
    blank: 'bl',
    notBlank: 'nbl',
};

function serialiseCondition(c: FilterCondition): string {
    if (c.type === 'date' && c.op === 'preset') return `pre:${c.preset}`;
    const code =
        c.type === 'text'
            ? TEXT_CODES[c.op]
            : c.type === 'number'
              ? NUMBER_CODES[c.op]
              : DATE_CODES[c.op];
    if (c.op === 'blank' || c.op === 'notBlank') return code;
    if (c.type === 'number') {
        const a = String(c.value);
        return c.op === 'between' ? `${code}:${a}~${String(c.to)}` : `${code}:${a}`;
    }
    if (c.type === 'date') {
        return c.op === 'between'
            ? `${code}:${c.value ?? ''}~${c.to ?? ''}`
            : `${code}:${c.value ?? ''}`;
    }
    return `${code}:${enc(c.value ?? '')}`;
}

/** The URL text of a filter. */
export function serialiseColumnFilter(f: ColumnFilter): string {
    if (f.kind === 'values') {
        const parts = f.values.map(enc);
        if (f.includeBlanks) parts.push(BLANK_MARK);
        return `v:${parts.join('|')}`;
    }
    const first = serialiseCondition(f.first);
    if (!f.second) return first;
    return `${f.join === 'or' ? 'o' : 'a'}:${first};${serialiseCondition(f.second)}`;
}

function reverse<V extends string>(codes: Record<V, string>): Map<string, V> {
    return new Map((Object.entries(codes) as [V, string][]).map(([op, code]) => [code, op]));
}
const TEXT_BY_CODE = reverse(TEXT_CODES);
const NUMBER_BY_CODE = reverse(NUMBER_CODES);
const DATE_BY_CODE = reverse(DATE_CODES);
const PRESET_NAMES: readonly string[] = DATE_PRESETS.map((p) => p.preset);

function parseCondition(raw: string, type: FilterType): FilterCondition | null {
    if (raw === 'bl' || raw === 'nbl') {
        const op = raw === 'bl' ? 'blank' : 'notBlank';
        if (type === 'number') return { type: 'number', op };
        if (type === 'date') return { type: 'date', op };
        return { type: 'text', op };
    }
    const cut = raw.indexOf(':');
    if (cut < 0) return null;
    const code = raw.slice(0, cut);
    const body = raw.slice(cut + 1);
    if (type === 'date') {
        if (code === 'pre') {
            return PRESET_NAMES.includes(body)
                ? { type: 'date', op: 'preset', preset: body as DatePreset }
                : null;
        }
        const op = DATE_BY_CODE.get(code);
        if (!op || op === 'blank' || op === 'notBlank') return null;
        const cond: FilterCondition =
            op === 'between'
                ? { type: 'date', op, value: body.split('~')[0], to: body.split('~')[1] }
                : { type: 'date', op, value: body };
        return isFilterComplete(cond) ? cond : null;
    }
    if (type === 'number') {
        const op = NUMBER_BY_CODE.get(code);
        if (!op || op === 'blank' || op === 'notBlank') return null;
        const parts = body.split('~');
        const value = parts[0] === '' ? Number.NaN : Number(parts[0]);
        if (op === 'between') {
            const to = parts[1] === undefined || parts[1] === '' ? Number.NaN : Number(parts[1]);
            const cond: FilterCondition = { type: 'number', op, value, to };
            return isFilterComplete(cond) ? cond : null;
        }
        if (parts.length !== 1) return null;
        const cond: FilterCondition = { type: 'number', op, value };
        return isFilterComplete(cond) ? cond : null;
    }
    if (type === 'enum') return null;
    const op = TEXT_BY_CODE.get(code);
    if (!op || op === 'blank' || op === 'notBlank') return null;
    const value = dec(body);
    if (value === null) return null;
    const cond: FilterCondition = { type: 'text', op, value };
    return isFilterComplete(cond) ? cond : null;
}

/** Read a filter from URL text. Bad input gives null, and the caller ignores it. */
export function parseColumnFilter(raw: string, type: FilterType): ColumnFilter | null {
    if (raw.startsWith('v:')) {
        const body = raw.slice(2);
        const values: string[] = [];
        let includeBlanks = false;
        if (body !== '') {
            for (const part of body.split('|')) {
                if (part === BLANK_MARK) {
                    includeBlanks = true;
                    continue;
                }
                const value = dec(part);
                if (value === null) return null;
                values.push(value);
            }
        }
        return { kind: 'values', values, includeBlanks };
    }
    const joined = /^([ao]):(.*)$/.exec(raw);
    if (joined) {
        const halves = joined[2].split(';');
        if (halves.length !== 2) return null;
        const first = parseCondition(halves[0], type);
        const second = parseCondition(halves[1], type);
        if (!first || !second) return null;
        return { kind: 'conditions', first, join: joined[1] === 'o' ? 'or' : 'and', second };
    }
    const first = parseCondition(raw, type);
    return first ? { kind: 'conditions', first } : null;
}

/** A stable text of a whole model. Use it as a memo key, not object identity. */
export function serialiseModel(model: ColumnFilterModel): string {
    return Object.keys(model)
        .sort()
        .map((id) => `${id}=${serialiseColumnFilter(model[id])}`)
        .join('\n');
}

/**
 * Give each column the filter type that a shared map states. The same map goes
 * to `useTableViewState`, so the type that reads a URL and the type that
 * filters the rows are one type. A column that the map does not list gets no
 * funnel: the URL cannot hold its filter, so the filter would do nothing and
 * give no message.
 */
export function pinFilterTypes<T>(
    columns: readonly DataTableColumn<T>[],
    types: Readonly<Record<string, FilterType>>,
): DataTableColumn<T>[] {
    return columns.map((c) => {
        if (types[c.id]) return { ...c, filterType: types[c.id] };
        return c.filterable === false ? c : { ...c, filterable: false };
    });
}
